import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { WHATSAPP_URL } from "@/data/content";

export default function PriceMenu() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const plans = t.pricing.plans;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="price-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-ink hover:text-brand transition-colors duration-300"
        aria-expanded={open}
      >
        <span className="hidden sm:inline">{t.priceMenu.label}</span>
        <span className="sm:hidden">{t.priceMenu.title}</span>
        <ChevronDown size={14} strokeWidth={2} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="price-menu-panel"
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
            className="absolute right-0 top-full mt-4 w-72 bg-cream/90 backdrop-blur-xl border border-ink/10 rounded-tl-2xl rounded-br-2xl shadow-[0_20px_60px_rgba(45,42,38,0.15)] p-6 z-50"
          >
            <p className="text-[0.65rem] tracking-[0.2em] uppercase text-brand mb-4">{t.priceMenu.title}</p>
            {plans.map((p, i) => (
              <div
                key={i}
                data-testid={`price-menu-plan-${i}`}
                className={`flex items-baseline justify-between py-3 ${i === 0 ? "border-b border-ink/10" : ""}`}
              >
                <span className="text-sm tracking-[0.1em] uppercase text-ink-soft">{p.duration}</span>
                <span className="font-serif text-3xl text-ink">{p.price}</span>
              </div>
            ))}
            <a
              data-testid="price-menu-cta"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center justify-center rounded-full bg-ink text-cream text-xs tracking-[0.15em] uppercase px-6 py-3 hover:bg-brand transition-colors duration-400"
            >
              {t.priceMenu.cta}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
